import React from 'react';
import { api, type MarkSkjutmeny } from '../api';
import { groupByFamily, kr, statusText } from '../lib/skjutmeny';
import type { Theme } from '../theme';
import { GameStatusIcon } from './GameStatusIcon';

// Skjutmenyns fågelsektion (backlogg SM2): alla fåglar med allmän jakttid i markens län
// (SFS 1987:905 bilaga 1, räknas i API:t), grupperade. Fågel slås på/av som grupp för marken
// av den som får ändra viltvårdsplanen; övriga ser bara läget.

interface Props {
  markId: string;
  birds: MarkSkjutmeny['birds'];
  county: string | null;
  T: Theme;
  /** Hämta om skjutmenyn efter att fågel slagits på/av. */
  onChanged: () => void;
}

export function SkjutmenyFagel({ markId, birds, county, T, onChanged }: Props) {
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [open, setOpen] = React.useState(true);
  const [openRow, setOpenRow] = React.useState<string | null>(null);
  const groups = React.useMemo(() => groupByFamily(birds.rows), [birds.rows]);
  const lovliga = birds.rows.filter(r => r.status === 'lovligt').length;

  const toggle = async () => {
    setSaving(true); setError(null);
    try { await api.setSkjutmenyBirds(markId, !birds.enabled); onChanged(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Kunde inte spara'); }
    finally { setSaving(false); }
  };

  return (
    <section style={{ marginTop: 16, borderTop: `1px solid ${T.border}`, paddingTop: 10 }} aria-label="Fågel">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          disabled={!birds.enabled}
          aria-expanded={birds.enabled ? open : undefined}
          style={{ flex: 1, display: 'flex', alignItems: 'baseline', gap: 8, background: 'none', border: 'none', padding: '4px 0', color: T.text, cursor: birds.enabled ? 'pointer' : 'default', fontFamily: 'inherit', textAlign: 'left' }}
        >
          <span style={{ fontWeight: 800, fontSize: 14 }}>Fågel</span>
          <span style={{ color: T.textMuted, fontSize: 11 }}>
            {birds.enabled ? `${lovliga} lovliga nu av ${birds.rows.length}` : 'avstängd'}
            {birds.enabled && <> · {open ? 'dölj' : 'visa'}</>}
          </span>
        </button>
        {birds.canToggle && (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.textSub, cursor: saving ? 'wait' : 'pointer' }}>
            <input type="checkbox" role="switch" checked={birds.enabled} disabled={saving} onChange={toggle} aria-label="Fågel på marken" style={{ accentColor: T.amber, width: 16, height: 16 }} />
            {birds.enabled ? 'På' : 'Av'}
          </label>
        )}
      </div>
      {error && <div style={{ color: T.danger, fontSize: 12, marginTop: 4 }}>{error}</div>}

      {!birds.enabled ? (
        <div style={{ color: T.textSub, fontSize: 12, lineHeight: 1.45, marginTop: 2 }}>
          Fågel jagas inte på marken.{birds.canToggle ? ` Slå på för att visa alla fåglar med jakttid i ${county ?? 'länet'}.` : ''}
        </div>
      ) : open && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 4 }}>
          <tbody>
            {groups.map(group => (
              <React.Fragment key={group.familyKey}>
                <tr>
                  <td colSpan={4} style={{ padding: '8px 4px 3px', fontWeight: 700, color: T.textSub, fontSize: 12, letterSpacing: '0.2px' }}>{group.familyLabel}</td>
                </tr>
                {group.rows.map(r => {
                  const rowOpen = openRow === r.gameType;
                  const dim = r.status === 'ej_jakttid' || r.status === 'okand';
                  return (
                    <React.Fragment key={r.gameType}>
                      <tr onClick={() => setOpenRow(rowOpen ? null : r.gameType)} title={r.reason} style={{ borderTop: `1px solid ${T.border}`, cursor: 'pointer', opacity: dim ? 0.75 : 1 }}>
                        <td style={{ width: 22, padding: '7px 6px 7px 4px' }}><GameStatusIcon status={r.status} T={T} title={r.reason} /></td>
                        <td style={{ padding: '7px 4px 7px 14px', color: T.text }}>
                          {r.gameType}
                          {r.status !== 'lovligt' && <div style={{ color: T.textMuted, fontSize: 11, marginTop: 1 }}>{statusText(r)}</div>}
                        </td>
                        <td style={{ textAlign: 'right', padding: '7px 4px', color: T.textSub, fontVariantNumeric: 'tabular-nums' }}>{r.planned != null ? `${r.remaining} av ${r.planned}` : '—'}</td>
                        <td style={{ textAlign: 'right', padding: '7px 4px', color: T.textSub, fontSize: 12, whiteSpace: 'nowrap' }}>{r.fieldFee != null ? `${r.feeFrom ? 'från ' : ''}${kr(r.fieldFee)}` : '—'}</td>
                      </tr>
                      {rowOpen && (
                        <tr>
                          <td />
                          <td colSpan={3} style={{ padding: '0 4px 9px 14px', color: T.textSub, fontSize: 12, lineHeight: 1.45 }}>
                            <div style={{ color: T.text }}>{r.reason}</div>
                            {r.notes.map(n => <div key={n}>· {n}</div>)}
                            {r.periods.length > 0 && <div style={{ marginTop: 4, color: T.textMuted }}>Jakttider i {county ?? 'länet'}: {r.periods.join('; ')}</div>}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
